import * as _ from 'underscore';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/TZUtils';
import { DomainSummary } from 'data/models/DomainSummary';
import { DateTimeDisplaySettings } from 'data/models/DateTimeDisplaySettings';
import { DateDisplayOrder } from 'server/enums/DateDisplayOrder';

export function LocalizationContext(tzutils : TZUtils, dateTimeSettings : DateTimeDisplaySettings) {
    return {
        tzutils: tzutils,
        DateFormatDefaults: DateFormatDefaults(dateTimeSettings)
    };
}
export type LocalizationContext = ReturnType<typeof LocalizationContext>;

// Default date/time formatting.
// Takes into account culture info on DomainSettings.
// Individual DateOrTimePickerBase instances can still override these defaults.
//
// TH: This is all based on a roll-our-own approach to localization, w/ custom settings in DomainSettings.
// Medium term, we might wanna switch to a more out-of-the-box solution based on the localization tools already provided by .NET, momentjs, and Kendo UI.
// Also, as of 4/20/2016, only the datepickers have been localized.
// For consistency, we need to localize all date displays in the UI and in other messaging like workflow emails and operational notifications.
function DateFormatDefaults(dateTimeSettings : DateTimeDisplaySettings) {
    // 1. Date formatting...
    // Kendo-specific date formatting tokens
    const kMonth = 'MM', kDay = 'dd', kFullYear = 'yyyy', kShortYear = 'yy';

    const dateComponents = () => {
        const dateOrder = dateTimeSettings.DateOrder();
        if (dateOrder === DateDisplayOrder.MonthDayYear) return [kMonth, kDay, kFullYear];
        if (dateOrder === DateDisplayOrder.DayMonthYear) return [kDay, kMonth, kFullYear];
        if (dateOrder === DateDisplayOrder.YearMonthDay) return [kFullYear, kMonth, kDay];
        throw new Error('Unknown DateDisplayOrder value: ' + dateOrder);
    }

    const dateFormat = () => dateComponents().join(dateTimeSettings.DateSeparator());

    const knownDateSeparators = () => _.uniq(['/', '-', '.', ''].concat(dateTimeSettings.DateSeparator()));

    const dateParseFormats = () => knownDateSeparators().reduce(function (formats, sep) {
        const withFullYear = dateComponents().join(sep),
            withShortYear = withFullYear.replace(kFullYear, kShortYear);
        return formats.concat(withFullYear, withShortYear);
    }, [] as string[]);


    // 2. Time formatting...
    // TH: 24-hour time only. Kendo format for 24-hour is 'HH:mm'.
    const timeComponents = ['HH', 'mm'];

    const timeFormat = () => timeComponents.join(dateTimeSettings.TimeSeparator());

    const knownTimeSeparators = () => _.uniq([':', '.', ''].concat(dateTimeSettings.TimeSeparator()));

    const timeParseFormats = () => knownTimeSeparators().map(function (sep) {
        return timeComponents.join(sep);
    }).concat('HH'); // Also allow entering hours w/ no minutes

    return {
        dateFormat: dateFormat,
        dateParseFormats: dateParseFormats,
        timeFormat: timeFormat,
        timeParseFormats: timeParseFormats
    };
}


// WEBPACK FOOTER //
// ./apps/common/models/LocalizationContext.ts